import React, { useEffect } from "react";
import { useLocation, Navigate, Routes, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AdminLayout from "./Layout/AdminLayout";
import AuthLayout from "./Layout/AuthLayout";
import {
  fetchUserDetails,
  rehydrateAuthState,
} from "./Redux/slice/features/authSlice";
import BookingSuccessPage from "./Page/BookingSuccess";
import BookingFailed from "./Partials/Universal/Hotels/Components/BookingFailed";
import ManagerProfile from "./Page/ManagerProfile";
import { fetchMenus } from "./Redux/slice/features/menuSlice";

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;

  const { isAuthenticated, loading, userType } = useSelector(
    (state) => state.auth
  );

  const themeSettings = useSelector((state) => ({
    themeMode: state.settings.themeMode,
    themeColor: state.settings.themeColor,
    borderStroke: state.settings.borderStroke,
    boxLayout: state.settings.boxLayout,
    monochrome: state.settings.monochrome,
    borderRadius: state.settings.borderRadius,
    iconColor: state.settings.iconColor,
    gradientColor: state.settings.gradientColor,
  }));

  const authRoutes = [
    "/signin",
    "/hrms/login",
    "/signup",
    "/password-reset",
    "/two-step",
    "/lockscreen",
    "/maintenance",
    "/404",
  ];

  // HRMS allowed routes (STRICT)
  const hrmsRoutes = [
    "/hrms",
    "/hrms/my-profile",
    "/employees-leave-report",
    "/management-profile-config",
    "/service-record",
  ];

  const isHrmsRoute = hrmsRoutes.some((route) => pathname.startsWith(route));

  const noSidebarRoutes = [/^\/booking-view\/[^/]+$/];

  const hideSidebar = noSidebarRoutes.some((pattern) =>
    typeof pattern === "string" ? pathname === pattern : pattern.test(pathname)
  );

  const hideHeader = hideSidebar;

  // Rehydrate auth state on mount
  useEffect(() => {
    dispatch(rehydrateAuthState());
  }, [dispatch]);

  // Fetch user details & menus when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserDetails());
      dispatch(fetchMenus());
    }
  }, [dispatch, isAuthenticated]);

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  const getRedirectPath = () => {
    if (!isAuthenticated) return "/signin";
    if (userType === "hrms") return "/hrms/my-profile";
    return "/";
  };

  const canAccessRoute = () => {
    if (authRoutes.includes(pathname)) return true;
    if (!isAuthenticated) return false;

    // HRMS users can only access HRMS routes
    if (userType === "hrms") {
      return true;
    }

    // Admin users can access all routes except HRMS login
    return pathname !== "/hrms/login";
  };

  return (
    <Routes>
      <Route
        path="/*"
        element={
          authRoutes.includes(pathname) ? (
            <AuthLayout />
          ) : isAuthenticated && canAccessRoute() ? (
            <AdminLayout
              {...themeSettings}
              hideSidebar={hideSidebar}
              hideHeader={hideHeader}
            />
          ) : isAuthenticated && !canAccessRoute() ? (
            <Navigate to={getRedirectPath()} replace />
          ) : (
            <Navigate
              to={
                pathname.startsWith("/hrms")
                  ? "/hrms/login"
                  : "/signin"
              }
              state={{ from: pathname }}
              replace
            />
          )
        }
      />

      <Route path="*" element={<Navigate to={getRedirectPath()} replace />} />

      <Route
        path="/booking-success/:orderId"
        element={<BookingSuccessPage />}
      />

      <Route path="/booking-failed" element={<BookingFailed />} />
      <Route path="/manager/profile" element={<ManagerProfile />} />
    </Routes>
  );
};

export default App;
